import { api, REPORT_RUN_TIMEOUT_MS } from './client'
import { requireArrayIn } from './readShape'
import { specializedJobsOn, runReportAsJob } from './reportJobRun'
import { responseStatus } from './upstreamStatus'

// CC-50022: financial-statement runs share the extended report-run timeout —
// the global 30s abort turned a slow proc into "took too long" advice that
// narrowing the range can't fix.
const RUN = { timeout: REPORT_RUN_TIMEOUT_MS }

/**
 * The reason cb-api gave for not producing a file (`{ error }`), or null. Spec 154:
 * the Comparative P&L export is REFUSED when CB's file would disagree with the
 * screen, and the refusal says why and what to use instead — a bare status code
 * would hide exactly that. Only a JSON body's own `error` string is used.
 */
const refusalText = async (resp) => {
  try {
    if (!String(resp.headers?.get?.('content-type') || '').includes('application/json')) return null
    const body = await resp.json()
    const text = typeof body?.error === 'string' ? body.error.trim() : ''
    return text ? text.slice(0, 500) : null
  } catch {
    return null
  }
}

const downloadFile = async (url, getToken, fallbackName, { sayWhy = false } = {}) => {
  const token = await getToken()
  const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
  if (!resp.ok) throw new Error((sayWhy && await refusalText(resp)) || `Export failed: ${responseStatus(resp)}`)
  const blob = await resp.blob()
  const dispo = resp.headers.get('content-disposition') || ''
  const m = /filename="?([^"]+)"?/.exec(dispo)
  const filename = m?.[1] || fallbackName
  const objUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(objUrl)
}

const buildQs = (params) => {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') qs.set(k, String(v))
  }
  return qs.toString()
}

/**
 * The `{ method, path }` replay spec for an accounting report's GET endpoint.
 *
 * Exported so a statement page's QUEUE control can describe the same request its
 * own Run makes, without re-implementing `buildQs`. Two copies of a query-string
 * builder is precisely how a queued run drifts from an interactive one — and CB
 * answers a dropped param with a differently-scoped report rather than an error
 * (CC-49833). One builder, one contract.
 */
export const accountingEndpoint = (path, params) => {
  const qs = buildQs(params)
  return { method: 'GET', path: qs ? `${path}?${qs}` : path }
}

/**
 * Spec 194 — the file formats a queued or scheduled run of the statement at `path` (its
 * run path, as passed to `accountingEndpoint`) can be emailed in. cb-api's mailer attaches
 * the page's own server export for these, and nothing else (cb-api
 * `jobs/specializedExport.js` `ATTACHABLE_EXPORTS`, which a test keeps this table equal
 * to). GL Budget is CSV only because CB has no Excel action for it. Any other path: none,
 * so the queue offers a link only.
 */
const ATTACH_FORMATS = Object.freeze({
  '/reports/accounting/balance-sheet/data': Object.freeze(['xlsx', 'csv']),
  '/reports/accounting/general-ledger/data': Object.freeze(['xlsx', 'csv']),
  '/reports/accounting/pnl': Object.freeze(['xlsx', 'csv']),
  '/reports/accounting/trial-balance': Object.freeze(['xlsx', 'csv']),
  '/reports/accounting/gl-budget': Object.freeze(['csv']),
  // Spec 200 — Matter Metrics queues through accountingEndpoint too; its file is CB's
  // own download for the run's query (cb-api `/matter-metrics/report/export.*`).
  '/reports/matter-metrics/report': Object.freeze(['xlsx', 'csv']),
})
const NO_FORMATS = Object.freeze([])
export const accountingAttachFormats = (path) => (Object.prototype.hasOwnProperty.call(ATTACH_FORMATS, path) ? ATTACH_FORMATS[path] : NO_FORMATS)
export const _attachFormatsTable = ATTACH_FORMATS

// Run an accounting report as a background job (spec 055): the server replays this
// report's own GET endpoint off the request path (params ride in the query string,
// same as the direct call and the CSV export). `accountingKey` drives the data-security
// class server-side — P&L is firm-shareable; GL / Balance Sheet / Trial Balance / GL-Budget
// stay per-user (bank-account security). Returns `result.data` (the same shape run() returns).
async function runAccountingViaJob(accountingKey, path, params, fallback, reportId) {
  const qs = buildQs(params)
  const result = await runReportAsJob({
    kind: 'accounting',
    accountingKey,
    reportId, // carried on the job so the "My Reports" tray can link back to /reports/:id
    filters: params, // dedupe input (canonicalized server-side)
    endpoint: { method: 'GET', path: qs ? `${path}?${qs}` : path },
  })
  return result?.data ?? fallback
}

const accountingReportService = {
  // ----- Trial Balance -----
  trialBalance: {
    async run(params, opts = {}) {
      if (specializedJobsOn()) return runAccountingViaJob('trial-balance', '/reports/accounting/trial-balance', params, null, opts.reportId)
      const data = await api.get('/reports/accounting/trial-balance', params, RUN)
      return data?.data || null
    },
    download(params, format, getToken) {
      const url = `/api/v1/reports/accounting/trial-balance/export.${format}?${buildQs(params)}`
      return downloadFile(url, getToken, `trial-balance.${format}`)
    },
  },

  // ----- Profit & Loss -----
  pnl: {
    async run(params, opts = {}) {
      if (specializedJobsOn()) return runAccountingViaJob('pnl', '/reports/accounting/pnl', params, null, opts.reportId)
      const data = await api.get('/reports/accounting/pnl', params, RUN)
      return data?.data || null
    },
    download(params, format, getToken) {
      const url = `/api/v1/reports/accounting/pnl/export.${format}?${buildQs(params)}`
      // Spec 154: a refused Comparative file says why (see refusalText).
      return downloadFile(url, getToken, `pnl.${format}`, { sayWhy: true })
    },
  },

  // ----- GL Budget vs Actual -----
  glBudget: {
    async run(params, opts = {}) {
      if (specializedJobsOn()) return runAccountingViaJob('gl-budget', '/reports/accounting/gl-budget', params, null, opts.reportId)
      const data = await api.get('/reports/accounting/gl-budget', params, RUN)
      return data?.data || null
    },
    async getBudgetHeaders() {
      const data = await api.get('/reports/accounting/gl-budget-headers')
      return data?.data || []
    },
    // CSV export proxied via cb-api to the legacy
    // Accounting/DownloadGLBudgetVsActualCSV endpoint (no Excel variant in CB).
    download(params, format, getToken) {
      const url = `/api/v1/reports/accounting/gl-budget/export.${format}?${buildQs(params)}`
      return downloadFile(url, getToken, `gl-budget.${format}`)
    },
  },

  // ----- Balance Sheet -----
  balanceSheet: {
    async run(params, opts = {}) {
      if (specializedJobsOn()) return runAccountingViaJob('balance-sheet', '/reports/accounting/balance-sheet/data', params, { headers: [], rows: [] }, opts.reportId)
      const data = await api.get('/reports/accounting/balance-sheet/data', params, RUN)
      return data?.data || { headers: [], rows: [] }
    },
    download(params, format, getToken) {
      const url = `/api/v1/reports/accounting/balance-sheet/export.${format}?${buildQs(params)}`
      return downloadFile(url, getToken, `balance-sheet.${format}`)
    },
  },

  // ----- General Ledger -----
  generalLedger: {
    async run(params, opts = {}) {
      if (specializedJobsOn()) return runAccountingViaJob('general-ledger', '/reports/accounting/general-ledger/data', params, { headers: [], rows: [] }, opts.reportId)
      const data = await api.get('/reports/accounting/general-ledger/data', params, RUN)
      return data?.data || { headers: [], rows: [] }
    },
    download(params, format, getToken) {
      const url = `/api/v1/reports/accounting/general-ledger/export.${format}?${buildQs(params)}`
      return downloadFile(url, getToken, `general-ledger.${format}`)
    },
  },

  // ----- GL categories (CC-49973) -----
  // Shared by the Category variants of P&L / Balance Sheet / Trial Balance.
  // cb-api answers BOTH questions in one call so the AccountingTrackCategories
  // setting and the "empty list means not tracking" rule can never disagree
  // between two pages. Fails closed to "not tracking" (never throws) —
  // callers treat that as "don't offer category reporting".
  categories: {
    async list() {
      try {
        const data = await api.get('/reports/accounting/categories')
        const d = data?.data || {}
        return {
          trackCategories: !!d.trackCategories,
          categories: Array.isArray(d.categories) ? d.categories : [],
        }
      } catch {
        return { trackCategories: false, categories: [] }
      }
    },
  },

  // ----- Reconciliation History -----
  reconciliationHistory: {
    async run({ accountId, startDate, endDate }) {
      const data = await api.get('/reports/accounting/reconciliation-history', { accountId, startDate, endDate })
      // CC-53052: an unreadable body must not render as a reconciliation history with
      // no reconciliations. A genuinely empty history still returns [].
      return requireArrayIn(data, 'rows', { noun: 'reconciliation history', emptyNoun: 'history' })
    },
    async getBankAccounts() {
      const data = await api.get('/reports/accounting/accounts-list', { accountCategory: 'Asset' })
      return data?.data || []
    },
  },

  // ----- Matter Metrics -----
  matterMetrics: {
    async filter(filter) {
      const data = await api.post('/reports/matter-metrics/filter', filter)
      return data?.data || null
    },
    async getData(payload) {
      const data = await api.post('/reports/matter-metrics/data', payload)
      return data?.data || null
    },
    async canAccess() {
      const data = await api.get('/reports/matter-metrics/can-access')
      return !!data?.data?.canAccess
    },
    // CC-50238: the rendered report. CB builds the whole period/actual-budget
    // matrix for its own export, so cb-api fetches that CSV and parses it into
    // the standard { headers, rows, meta } shape (Balance Sheet / GL pattern)
    // rather than cb2 reimplementing V1's pivot builder. Replaces the
    // filter + getData two-hop, which could not render this report at all.
    async report(params) {
      const data = await api.get('/reports/matter-metrics/report', params)
      return data?.data || { headers: [], rows: [], meta: [] }
    },
    // CC-49900: the Practice Area names V1's page offers (field 1001:1054).
    async practiceAreas() {
      const data = await api.get('/reports/matter-metrics/practice-areas')
      return Array.isArray(data?.data) ? data.data : []
    },
  },
}

export default accountingReportService
